import { tg } from "./telegram";

export interface Post {
  id: number;
  authorId: number | null;
  isAnonymous: boolean;
  type: "text" | "media" | "graffiti" | "gift";
  body: string | null;
  status: "visible" | "pending" | "hidden";
  createdAt: string;
  mediaKey?: string | null;
  graffiti?: GraffitiStroke[] | null;
  rarity?: "common" | "rare" | "epic" | "legendary" | null;
  edition?: number | null;
  signature?: boolean | null;
  authorName?: string | null;
  authorVerified?: boolean | null;
  authorTitle?: string | null;
}

export interface Wall {
  id: number;
  slug: string;
  type: string;
  title: string | null;
  privacy: string;
}

export interface GraffitiStroke {
  color: string;
  width: number;
  opacity?: number;
  points: [number, number][];
}

export interface NewPost {
  type: "text" | "graffiti" | "media";
  body?: string;
  mediaKey?: string;
  isAnonymous: boolean;
  asGift?: boolean;
  graffiti?: { strokes: GraffitiStroke[]; isAiAssisted: boolean };
}

export interface CreatedPost {
  ok: boolean;
  postId: number;
  status: "visible" | "pending" | "hidden";
  edition?: number;
  rarity?: "common" | "rare" | "epic" | "legendary";
  signature?: boolean;
}

function authHeaders(): HeadersInit {
  return {
    "content-type": "application/json",
    "x-telegram-init-data": tg?.initData ?? "",
  };
}

export async function fetchWall(slug: string): Promise<{ wall: Wall; posts: Post[] }> {
  const res = await fetch(`/api/wall/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`Wall not found (${res.status})`);
  return res.json() as Promise<{ wall: Wall; posts: Post[] }>;
}

export async function createPost(slug: string, post: NewPost): Promise<CreatedPost> {
  const res = await fetch(`/api/wall/${encodeURIComponent(slug)}/post`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(post),
  });
  if (!res.ok) throw new Error(`Failed to post (${res.status})`);
  return res.json() as Promise<CreatedPost>;
}

export async function reportPost(
  postId: number,
): Promise<{ ok: boolean; reports: number; hidden: boolean }> {
  const res = await fetch(`/api/post/${postId}/report`, { method: "POST", headers: authHeaders() });
  if (!res.ok) throw new Error(`report failed (${res.status})`);
  return res.json() as Promise<{ ok: boolean; reports: number; hidden: boolean }>;
}

export async function createGift(
  slug: string,
  stars: number,
  emoji: string,
): Promise<{ link: string }> {
  const res = await fetch(`/api/wall/${encodeURIComponent(slug)}/gift`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ stars, emoji }),
  });
  if (!res.ok) throw new Error(`gift failed (${res.status})`);
  return res.json() as Promise<{ link: string }>;
}

export async function createMint(postId: number): Promise<{ link: string }> {
  const res = await fetch(`/api/post/${postId}/mint`, { method: "POST", headers: authHeaders() });
  if (!res.ok) throw new Error(`mint failed (${res.status})`);
  return res.json() as Promise<{ link: string }>;
}

export async function aiImage(prompt: string, style?: string): Promise<{ dataUrl: string }> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 90000);
  try {
    const res = await fetch("/api/ai/image", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ prompt, style }),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`ai image failed (${res.status})`);
    return res.json() as Promise<{ dataUrl: string }>;
  } finally {
    clearTimeout(timer);
  }
}

export async function uploadImage(dataUrl: string): Promise<{ mediaKey: string }> {
  const res = await fetch("/api/media/upload", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ dataUrl }),
  });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
  return res.json() as Promise<{ mediaKey: string }>;
}

export async function createGiftDraft(mediaKey: string): Promise<{ preparedMessageId: string }> {
  const res = await fetch("/api/gift/draft", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ mediaKey }),
  });
  if (!res.ok) throw new Error(`gift draft failed (${res.status})`);
  return res.json() as Promise<{ preparedMessageId: string }>;
}

export async function claimGift(
  token: string,
): Promise<{ ok: boolean; slug?: string; edition?: number; already?: boolean }> {
  const res = await fetch("/api/gift/claim", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error(`claim failed (${res.status})`);
  return res.json() as Promise<{ ok: boolean; slug?: string; edition?: number; already?: boolean }>;
}

export async function aiGraffiti(prompt: string): Promise<GraffitiStroke[]> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 75000);
  try {
    const res = await fetch("/api/ai/graffiti", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ prompt }),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`ai failed (${res.status})`);
    const data = (await res.json()) as { strokes: GraffitiStroke[] };
    return data.strokes;
  } finally {
    clearTimeout(timer);
  }
}

export interface WallRef {
  slug: string;
  title: string | null;
  type: string;
  count?: number;
}

export async function fetchDirectory(): Promise<WallRef[]> {
  const res = await fetch("/api/walls");
  if (!res.ok) return [];
  const data = (await res.json()) as { walls?: WallRef[] };
  return data.walls ?? [];
}

export async function fetchHot(): Promise<WallRef[]> {
  const res = await fetch("/api/walls/hot");
  if (!res.ok) return [];
  const data = (await res.json()) as { walls?: WallRef[] };
  return data.walls ?? [];
}
